import { useEffect, useState, useLayoutEffect } from "react"
import { Link, useLoaderData, useLocation, useNavigate, useOutletContext } from "react-router-dom"

import axios from "axios"
import { useAuth } from "contexts/auth-context"
import Form from "./form"
import nProgress from "nprogress"
import toast from "react-hot-toast"
import { useSWRConfig } from "swr"
import { usePage } from "contexts/page-context"
import { Transition } from "components"
import moment from "moment"
import { useAuditLogContext } from "contexts/audit-log-context"

/** Bangun FormData dengan key bracket (images[i][file]) */
function buildFormData(data) {
  const fd = new FormData()

  // metode PATCH via form-data
  fd.append("_method", "PATCH")

  // field model
  fd.append("product_series_id", data.product_series_id ?? "")
  if (data.product_series?.series?.name) {
    fd.append("product_series", data.product_series.series.name)
  }
  fd.append("name", data.name ?? "")
  fd.append("long_name", data.long_name ?? "")
  fd.append("description", data.description ?? "")
  if (data.updated_by) fd.append("updated_by", data.updated_by)

  // tags (array sederhana berisi slug/string)
  ;(data.tags ?? []).forEach((t, i) => {
    const v = typeof t === "string" ? t : (t?.slug ?? "")
    if (v !== "") fd.append(`tags[${i}]`, v)
  })

  // images
  ;(data.images ?? []).forEach((img, i) => {
    if (img.id) fd.append(`images[${i}][id]`, img.id)
    fd.append(`images[${i}][_action]`, img._action ?? "none")
    fd.append(`images[${i}][alt_text]`, img.alt_text ?? "")
    fd.append(`images[${i}][is_cover]`, img.is_cover ? 1 : 0)
    fd.append(
      `images[${i}][sort_number]`,
      Number.isFinite(img.sort_number) ? img.sort_number : i
    )
    if (img.file instanceof File) {
      fd.append(`images[${i}][file]`, img.file, img.file.name)
    }
  })

  return fd
}

/** Payload aman untuk audit log (tanpa binary) */
function safeAuditPayload(raw) {
  return {
    product_series_id: raw.product_series_id ?? null,
    product_series: raw.product_series?.series?.name ?? null,
    name: raw.name ?? null,
    long_name: raw.long_name ?? null,
    description: raw.description ?? null,
    updated_by: raw.updated_by ?? null,
    tags: (raw.tags ?? []).map((t) => (typeof t === "string" ? t : t?.slug ?? "")),
    images: (raw.images ?? []).map((x) => ({
      id: x.id ?? null,
      alt_text: x.alt_text ?? "",
      is_cover: !!x.is_cover,
      sort_number: x.sort_number ?? null,
      _action: x._action ?? "none",
      file_name: x.file instanceof File ? x.file.name : null,
      file_size: x.file instanceof File ? x.file.size : null,
      file_type: x.file instanceof File ? x.file.type : null,
    })),
  }
}

export default function Edit() {
  const { user } = useAuth()
  const { cache } = useSWRConfig()
  const { queueAuditLog } = useAuditLogContext()
  const { setTransition } = usePage()
  const { setTitle } = useOutletContext()

  const loaderData = useLoaderData()
  const location = useLocation()
  const navigate = useNavigate()

  const [prevRouteState, setPrevRouteState] = useState({})
  const [errors, setErrors] = useState({})

  useLayoutEffect(() => {
    const getPrevRouteState = async () => {
      setPrevRouteState({
        back: location.state?.back,
        from: location.state?.from,
        transition: location.state?.transition
      })
    }

    setTransition(location.state?.transition)
    setTitle(`Edit ${loaderData?.name}`)

    getPrevRouteState()
  }, [])

  const handleSubmit = async (data) => {
    setErrors({})
    nProgress.start()
    toast.loading("Loading", { id: "submit" })

    const url = `${process.env.REACT_APP_INVENTORY_API_URL}/product/model/${data?.id}`

    // data lama buat audit
    const oldData = {
      product_series_id: loaderData.product_series_id,
      product_series: loaderData.product_series?.series?.name,
      name: loaderData.name,
      long_name: loaderData.long_name,
      description: loaderData.description,
      updated_by: loaderData?.updated_by,
      tags: loaderData?.tags ?? [],
      // opsional: kirim ringkasan images lama
      images: (loaderData?.images ?? loaderData?.product_model_images ?? []).map((x) => ({
        id: x.id,
        alt_text: x.alt_text ?? "",
        is_cover: !!x.is_cover,
        sort_number: x.sort_number ?? null,
      })),
    }

    // payload baru (mentah) + FormData untuk request
    const rawPayload = {
      product_series_id: data.product_series_id,
      product_series: data.product_series, // object series (opsional)
      name: data.name,
      long_name: data.long_name,
      description: data.description,
      updated_by: user?.name,
      tags: data.tags,
      images: data.images, // dari UI multiple images (pakai _action)
    }

    const fd = buildFormData(rawPayload)

    try {
      // gunakan POST + _method=PATCH untuk multipart
      const response = await axios.post(url, fd)

      // Audit log (jangan kirim FormData)
      queueAuditLog(
        url,
        "PATCH",
        "Edit Product Model Data",
        user?.name,
        { old: oldData, new: safeAuditPayload(rawPayload) },
        null,
        moment().format("YYYY-MM-DD HH:mm:ss"),
        response.status
      )

      cache.clear()
      navigate({ pathname: "/product/model" })

      toast.success(response.data.message ?? "Updated", { id: "submit" })
    } catch (error) {
      if (error?.response?.status === 422) {
        setErrors(error.response.data.errors ?? {})
      }

      toast.error(error?.response?.data?.message || "Failed", {
        ariaProps: { superscript: error?.response?.status },
        id: "submit",
      })
    } finally {
      nProgress.done()
    }
  }

  return (
    <Transition type="slide" reversed={true}>
      <Form data={loaderData} errors={errors} onSubmit={handleSubmit} />
    </Transition>
  )
}
